import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  TEACHER_TRAINING_HOST_PROFILE_PATH,
  canManageOwnContent,
  teacherTrainingHostPath,
} from '../../config/contentRoutes';
import {
  FEATURED_TRAINING_PROFILES,
  type FeaturedTrainingProfile,
} from '../../data/featuredTrainingHosts';
import {
  formatTrainingHostError,
  subscribePublicProgramCountByHost,
  subscribePublicTrainingHosts,
} from '../../utils/trainingHost';
import type { TrainingHost } from '../../types/trainingHost';

type DirectoryProfile = {
  key: string;
  publicSlug: string;
  displayName: string;
  schoolName?: string;
  bio?: string;
  photoURL?: string;
  hostId?: string;
  legacyPath?: string;
  updatedAt?: Date;
  fromFirestore: boolean;
};

const publicProgramCount = (profile: DirectoryProfile, counts: Record<string, number>): number =>
  profile.hostId ? (counts[profile.hostId] ?? 0) : 0;

/** 공개 연수 수 우선, 동점이면 최근 업데이트 순 */
const activityScore = (profile: DirectoryProfile, counts: Record<string, number>): number => {
  const programs = publicProgramCount(profile, counts);
  const updated = profile.updatedAt?.getTime() ?? 0;
  return programs * 1_000_000_000_000 + updated;
};

const TrainingHostDirectory: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const canManage = canManageOwnContent(currentUser?.role);
  const [hosts, setHosts] = useState<TrainingHost[]>([]);
  const [programCounts, setProgramCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleCreateHub = () => {
    if (!currentUser) {
      navigate('/login', { state: { from: { pathname: TEACHER_TRAINING_HOST_PROFILE_PATH } } });
      return;
    }
    if (!canManage) {
      window.alert('교사 계정으로 로그인하면 내 연수 프로필을 만들 수 있습니다.');
      return;
    }
    navigate(TEACHER_TRAINING_HOST_PROFILE_PATH);
  };

  useEffect(() => {
    const unsubHosts = subscribePublicTrainingHosts(
      (items) => {
        setHosts(items);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(err);
        setError(formatTrainingHostError(err));
        setLoading(false);
      }
    );
    const unsubCounts = subscribePublicProgramCountByHost(setProgramCounts, (err) =>
      console.error(err)
    );
    return () => {
      unsubHosts();
      unsubCounts();
    };
  }, []);

  const profiles = useMemo((): DirectoryProfile[] => {
    const map = new Map<string, DirectoryProfile>();

    FEATURED_TRAINING_PROFILES.forEach((f: FeaturedTrainingProfile) => {
      map.set(f.publicSlug, {
        key: `featured-${f.publicSlug}`,
        publicSlug: f.publicSlug,
        displayName: f.displayName,
        schoolName: f.schoolName,
        bio: f.bio,
        photoURL: f.photoURL,
        legacyPath: f.legacyPath,
        fromFirestore: false,
      });
    });

    hosts.forEach((host) => {
      map.set(host.publicSlug, {
        key: host.id,
        publicSlug: host.publicSlug,
        displayName: host.displayName,
        schoolName: host.schoolName,
        bio: host.bio,
        photoURL: host.photoURL,
        hostId: host.id,
        updatedAt: host.updatedAt,
        fromFirestore: true,
      });
    });

    return Array.from(map.values()).sort((a, b) => {
      const scoreDiff = activityScore(b, programCounts) - activityScore(a, programCounts);
      if (scoreDiff !== 0) return scoreDiff;
      return a.displayName.localeCompare(b.displayName, 'ko');
    });
  }, [hosts, programCounts]);

  const openProfile = (profile: DirectoryProfile) => {
    if (profile.legacyPath) {
      navigate(profile.legacyPath);
      return;
    }
    navigate(teacherTrainingHostPath(profile.publicSlug));
  };

  const programCountLabel = (profile: DirectoryProfile) => {
    const count = publicProgramCount(profile, programCounts);
    if (!profile.hostId && profile.legacyPath) return null;
    if (count === 0) return '연수 준비 중';
    return `공개 연수 ${count}개`;
  };

  const isActiveTeacher = (profile: DirectoryProfile, index: number) =>
    index < 3 && publicProgramCount(profile, programCounts) > 0;

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <section
        style={{
          background: 'linear-gradient(135deg, #293044 0%, #3d4a66 55%, #FF6F0F 160%)',
          color: 'var(--Color-text-inverse)',
          padding: '40px 24px 48px',
        }}
      >
        <div style={{ maxWidth: '1040px', margin: '0 auto' }}>
          <p
            style={{
              margin: '0 0 8px',
              fontSize: '14px',
              fontWeight: 500,
              opacity: 0.9,
              letterSpacing: '0.02em',
            }}
          >
            MARU 교사 연수
          </p>
          <h1
            style={{
              margin: '0 0 12px',
              fontSize: 'clamp(28px, 5vw, 36px)',
              fontWeight: 700,
              lineHeight: 1.3,
            }}
          >
            교사 연수
          </h1>
          <p
            style={{
              margin: '0 0 24px',
              fontSize: '16px',
              lineHeight: 1.7,
              opacity: 0.92,
              maxWidth: '520px',
            }}
          >
            연수를 진행하는 교사들의 프로필을 모아 둔 공간입니다. 프로필을 눌러 각 교사의 연수를 확인하세요.
          </p>
          <Button
            variant="primary"
            size="medium"
            onClick={handleCreateHub}
            style={{ background: 'white', color: '#293044', border: 'none' }}
          >
            {canManage ? '내 프로필 만들기' : currentUser ? '내 프로필 (교사 전용)' : '로그인 후 프로필 만들기'}
          </Button>
        </div>
      </section>

      <div
        style={{
          maxWidth: '1040px',
          margin: '0 auto',
          padding: '32px 24px 48px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: '0 0 6px', fontSize: '22px', fontWeight: 700, color: 'var(--Color-text-strong)' }}>
              연수 교사
            </h2>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
              공개 연수가 많고 최근에 활동한 교사 순으로 표시됩니다.
            </p>
          </div>
          {!loading && (
            <span style={{ fontSize: '14px', color: 'var(--Color-text-normal)' }}>
              {profiles.length}명
            </span>
          )}
        </div>

        {loading && (
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)' }}>프로필 불러오는 중…</p>
        )}

        {error && (
          <Card padding="16px" borderRadius="12px" background="var(--Color-background-normal)">
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)' }}>{error}</p>
          </Card>
        )}

        {!loading && !error && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '20px',
            }}
          >
            {profiles.map((profile, index) => (
              <Card
                key={profile.key}
                padding="0"
                borderRadius="20px"
                background="var(--Color-background-normal)"
                style={{
                  cursor: 'pointer',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: isActiveTeacher(profile, index)
                    ? '2px solid var(--Color-fill-brand-color)'
                    : '1px solid var(--Color-line-normal)',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                }}
                onClick={() => openProfile(profile)}
              >
                <div
                  style={{
                    height: '72px',
                    background: isActiveTeacher(profile, index)
                      ? 'linear-gradient(120deg, #FF6F0F 0%, #293044 100%)'
                      : 'linear-gradient(120deg, var(--Color-fill-normal) 0%, #4a5678 100%)',
                    position: 'relative',
                  }}
                >
                  {isActiveTeacher(profile, index) && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '12px',
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: 'rgba(255,255,255,0.92)',
                        color: 'var(--Color-fill-brand-color)',
                      }}
                    >
                      활발
                    </span>
                  )}
                </div>
                <div style={{ padding: '0 20px 20px', marginTop: '-36px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {profile.photoURL ? (
                    <img
                      src={profile.photoURL}
                      alt=""
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '3px solid var(--Color-background-normal)',
                        marginBottom: '12px',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        border: '3px solid var(--Color-background-normal)',
                        backgroundColor: 'var(--Color-fill-hover)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                        marginBottom: '12px',
                      }}
                    >
                      🎓
                    </div>
                  )}
                  <h3
                    style={{
                      margin: '0 0 4px',
                      fontSize: '18px',
                      fontWeight: 700,
                      color: 'var(--Color-text-strong)',
                    }}
                  >
                    {profile.displayName}
                  </h3>
                  {profile.schoolName && (
                    <p style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--Color-text-normal)' }}>
                      {profile.schoolName}
                    </p>
                  )}
                  {programCountLabel(profile) && (
                    <span
                      style={{
                        display: 'inline-block',
                        alignSelf: 'flex-start',
                        marginBottom: '10px',
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        backgroundColor: 'var(--Color-fill-hover)',
                        color: 'var(--Color-text-normal)',
                      }}
                    >
                      {programCountLabel(profile)}
                    </span>
                  )}
                  {profile.bio && (
                    <p
                      style={{
                        margin: '0 0 16px',
                        fontSize: '14px',
                        color: 'var(--Color-text-normal)',
                        lineHeight: 1.55,
                        flex: 1,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {profile.bio}
                    </p>
                  )}
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--Color-fill-brand-color)' }}>
                    프로필 보기 →
                  </span>
                </div>
              </Card>
            ))}

            <Card
              padding="20px"
              borderRadius="20px"
              background="var(--Color-background-normal)"
              style={{
                cursor: 'pointer',
                height: '100%',
                minHeight: '260px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                border: '2px dashed var(--Color-line-normal)',
              }}
              onClick={handleCreateHub}
            >
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>➕</div>
              <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
                내 프로필 등록
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.5 }}>
                교사라면 연수 프로필을 등록하고 연수를 공유할 수 있습니다.
              </p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingHostDirectory;
